// Admin workspace > Diagnostics > "Compute nodes" (platform_admin only;
// routers/compute.py admin_router, docs/compute_node.md).
//
//   - nodes: capabilities, client/kind bindings, last seen, current job;
//     create (the token is shown ONCE), rotate the token, revoke;
//   - remote jobs: recent hecras_run / hechms_run / selftest_remote rows,
//     with a detail view (log tail, summary.json, result files, result zip
//     download, cancel) that refreshes while the job is active;
//   - enqueue helpers: "Run HEC-RAS project on a compute node" (zip + plan
//     id), an HEC-HMS run, and a self-test that proves the loop end to end.
import { getJson, postForm, postJson, uploadErrorText } from "./api";
import { el } from "./legend";

interface NodeJob {
  id: string;
  kind: string;
  progress: number | null;
  message: string | null;
}

interface ComputeNode {
  id: number;
  name: string;
  token_hint: string | null;
  queues: string[];
  kinds: string[];
  client_ids: number[];
  max_jobs: number;
  notes: string | null;
  created_at: string;
  revoked: boolean;
  revoked_at: string | null;
  online: boolean;
  capabilities: string[];
  hostname: string | null;
  agent_version: string | null;
  platform: string | null;
  last_seen_at: string | null;
  last_ip: string | null;
  current_jobs: NodeJob[];
}

interface NodesResponse {
  nodes: ComputeNode[];
  remote_kinds: Record<string, string[]>;
  limits: { max_result_mb: number; max_input_mb: number };
  stale_seconds: number;
}

interface RemoteJob {
  id: string;
  kind: string;
  status: string;
  client_id: number | null;
  username: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  progress: number | null;
  message: string | null;
  error: string | null;
  host: string | null;
  attempts: number;
  cancel_requested: number;
  required_capabilities: string[];
  params: Record<string, unknown>;
  has_result: boolean;
  has_input?: boolean;
  // retention (maintenance task compute_retention): the input/result files
  // are deleted retention_days after the job finished; the row stays
  retention_days?: number;
  kept_until?: string | null;
  days_left?: number | null;
  expiring_soon?: boolean;
  results_purged_at?: string | null;
  result: { summary?: Record<string, unknown>; node?: string } | null;
  log?: string[];
  log_total_lines?: number;
  result_files?: { path: string; size: number }[];
  summary?: Record<string, unknown> | null;
}

let hostEl: HTMLElement | null = null;
let nodes: NodesResponse | null = null;
let jobs: RemoteJob[] = [];
let selected: RemoteJob | null = null;
let error = "";
let newToken: { name: string; token: string } | null = null;
let busy = false;
let pollTimer: number | null = null;

const ACTIVE = new Set(["queued", "running"]);

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "–";
}

function redraw(): void {
  if (hostEl && hostEl.isConnected) draw(hostEl);
}

async function load(): Promise<void> {
  try {
    const [n, j] = await Promise.all([
      getJson<NodesResponse>("/api/admin/compute/nodes"),
      getJson<{ jobs: RemoteJob[] }>("/api/admin/compute/jobs?limit=30"),
    ]);
    nodes = n;
    jobs = j.jobs;
    error = "";
  } catch (err) {
    error = `Couldn't load compute nodes: ${(err as Error).message}`;
  }
  redraw();
}

async function loadJob(id: string): Promise<void> {
  try {
    selected = await getJson<RemoteJob>(`/api/admin/compute/jobs/${id}?log_lines=300`);
  } catch (err) {
    error = `Couldn't load job: ${(err as Error).message}`;
  }
  schedulePoll();
  redraw();
}

function schedulePoll(): void {
  if (pollTimer !== null) window.clearTimeout(pollTimer);
  pollTimer = null;
  const active = (selected && ACTIVE.has(selected.status)) || jobs.some((j) => ACTIVE.has(j.status));
  if (!active || !hostEl?.isConnected) return;
  pollTimer = window.setTimeout(async () => {
    pollTimer = null;
    await load();
    if (selected) await loadJob(selected.id);
    else schedulePoll();
  }, 3000);
}

async function act(fn: () => Promise<void>): Promise<void> {
  if (busy) return;
  busy = true;
  redraw();
  try {
    await fn();
    error = "";
  } catch (err) {
    error = uploadErrorText(err, (err as Error).message);
  } finally {
    busy = false;
  }
  await load();
  schedulePoll();
}

function table(headers: string[]): HTMLTableElement {
  const t = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  for (const h of headers) head.append(el("th", {}, h));
  t.append(head);
  return t;
}

function scrollWrap(node: HTMLElement): HTMLElement {
  const wrap = el("div", { className: "admin-table-scroll" });
  wrap.append(node);
  return wrap;
}

function drawNodes(root: HTMLElement): void {
  root.append(el("h4", {}, "Nodes"));
  if (!nodes) {
    root.append(el("p", { className: "caption" }, error ? "Unavailable." : "Loading…"));
    return;
  }
  if (newToken) {
    const box = el("div", { className: "compute-token-box" });
    box.append(
      el("p", {}, `Token for ${newToken.name} — shown once. Paste it into the node's save-token prompt; it can't be retrieved again.`),
    );
    const code = el("input", { readOnly: true, value: newToken.token, className: "compute-token" });
    code.style.width = "100%";
    code.addEventListener("focus", () => code.select());
    const copy = el("button", {}, "Copy");
    copy.addEventListener("click", () => {
      void navigator.clipboard?.writeText(newToken?.token ?? "");
      copy.textContent = "Copied";
    });
    const done = el("button", {}, "Done (hide the token)");
    done.addEventListener("click", () => {
      newToken = null;
      redraw();
    });
    box.append(code, copy, done);
    root.append(box);
  }
  if (!nodes.nodes.length) {
    root.append(el("p", { className: "caption" }, "No compute nodes yet."));
  } else {
    const t = table(["Node", "State", "Host", "Capabilities", "Clients / kinds", "Last seen", "Current job", ""]);
    for (const n of nodes.nodes) {
      const tr = document.createElement("tr");
      const state = n.revoked ? "revoked" : n.online ? "online" : "offline";
      const job = n.current_jobs[0];
      const actions = el("td", {});
      if (!n.revoked) {
        const rotate = el("button", { disabled: busy }, "New token");
        rotate.title = "Issue a new token (the current one stops working at once)";
        rotate.addEventListener("click", () => {
          if (!window.confirm(`Issue a new token for ${n.name}? The current token stops working immediately.`)) return;
          void act(async () => {
            const r = await postJson<{ token: string; node: ComputeNode }>(`/api/admin/compute/nodes/${n.id}/rotate`, {});
            newToken = { name: r.node.name, token: r.token };
          });
        });
        const revoke = el("button", { disabled: busy }, "Revoke");
        revoke.addEventListener("click", () => {
          if (!window.confirm(`Revoke ${n.name}'s token? The node can no longer call in.`)) return;
          void act(async () => {
            await postJson(`/api/admin/compute/nodes/${n.id}/revoke`, {});
          });
        });
        actions.append(rotate, revoke);
      }
      tr.append(
        el("td", {}, `${n.name}${n.token_hint ? ` (${n.token_hint}…)` : ""}`),
        el("td", { className: n.online ? "" : "caption" }, state),
        el("td", { className: "caption" }, `${n.hostname ?? "–"}${n.agent_version ? ` · agent ${n.agent_version}` : ""}`),
        el("td", { className: "caption" }, n.capabilities.join(", ") || "–"),
        el("td", { className: "caption" }, `clients ${n.client_ids.join(", ")} · ${n.kinds.join(", ")} · max ${n.max_jobs}`),
        el("td", { className: "caption" }, when(n.last_seen_at)),
        el("td", { className: "caption" }, job ? `${job.kind} ${Math.round((job.progress ?? 0) * 100)}% ${job.message ?? ""}` : "–"),
        actions,
      );
      t.append(tr);
    }
    root.append(scrollWrap(t));
  }
  drawCreateForm(root);
}

function drawCreateForm(root: HTMLElement): void {
  const details = el("details", {});
  details.append(el("summary", {}, "Add a compute node"));
  const form = el("form", { className: "compute-form" });
  const name = el("input", { placeholder: "e.g. desktop-o4pnkep", required: true });
  const clients = el("input", { placeholder: "client ids, e.g. 1", value: "1" });
  const maxJobs = el("input", { type: "number", min: "1", max: "16", value: "1" });
  const kinds: HTMLInputElement[] = [];
  const kindBox = el("span", {});
  for (const k of Object.keys(nodes?.remote_kinds ?? {})) {
    const cb = el("input", { type: "checkbox", checked: true, value: k });
    kinds.push(cb);
    const lab = el("label", {});
    lab.append(cb, document.createTextNode(` ${k} `));
    kindBox.append(lab);
  }
  const submit = el("button", { type: "submit", disabled: busy }, "Create node + token");
  for (const [label, input] of [
    ["Name", name],
    ["Clients", clients],
    ["Max jobs", maxJobs],
  ] as [string, HTMLElement][]) {
    const lab = el("label", {}, `${label} `);
    lab.append(input);
    form.append(lab, document.createTextNode(" "));
  }
  form.append(kindBox, submit);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const ids = clients.value
      .split(/[,\s]+/)
      .filter(Boolean)
      .map(Number);
    void act(async () => {
      const r = await postJson<{ token: string; node: ComputeNode }>("/api/admin/compute/nodes", {
        name: name.value.trim(),
        client_ids: ids,
        kinds: kinds.filter((k) => k.checked).map((k) => k.value),
        max_jobs: Number(maxJobs.value) || 1,
      });
      newToken = { name: r.node.name, token: r.token };
    });
  });
  details.append(
    el(
      "p",
      { className: "caption" },
      "A node only receives jobs of the clients and kinds it is bound to, and only when its capabilities match. " +
        "Install it with scripts/compute_node/install_task.ps1 (docs/compute_node.md).",
    ),
    form,
  );
  root.append(details);
}

function drawEnqueue(root: HTMLElement): void {
  root.append(el("h4", {}, "Run on a compute node"));
  const lim = nodes?.limits.max_input_mb;
  // HEC-RAS
  const ras = el("form", { className: "compute-form" });
  const rasFile = el("input", { type: "file", accept: ".zip", required: true });
  const plan = el("input", { placeholder: "plan id, e.g. p01", required: true, size: 8 });
  const rasTimeout = el("input", { type: "number", min: "1", max: "1440", value: "120", size: 6 });
  const rasCaps = el("input", { placeholder: "extra capabilities (optional)", size: 22 });
  const rasBtn = el("button", { type: "submit", disabled: busy }, "Run HEC-RAS project");
  ras.append(
    el("strong", {}, "HEC-RAS: "),
    rasFile,
    document.createTextNode(" plan "),
    plan,
    document.createTextNode(" timeout (min) "),
    rasTimeout,
    document.createTextNode(" "),
    rasCaps,
    document.createTextNode(" "),
    rasBtn,
  );
  ras.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const f = rasFile.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    form.append("plan_id", plan.value.trim());
    form.append("timeout_minutes", rasTimeout.value || "120");
    if (rasCaps.value.trim()) form.append("capabilities", rasCaps.value.trim());
    void act(async () => {
      const job = await postForm<RemoteJob>("/api/admin/compute/jobs/hecras", form);
      await loadJob(job.id);
    });
  });
  // HEC-HMS
  const hms = el("form", { className: "compute-form" });
  const hmsFile = el("input", { type: "file", accept: ".zip", required: true });
  const run = el("input", { placeholder: "simulation run, e.g. Run 1", required: true, size: 16 });
  const hmsBtn = el("button", { type: "submit", disabled: busy }, "Run HEC-HMS");
  hms.append(el("strong", {}, "HEC-HMS: "), hmsFile, document.createTextNode(" run "), run, document.createTextNode(" "), hmsBtn);
  hms.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const f = hmsFile.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    form.append("run_name", run.value.trim());
    void act(async () => {
      const job = await postForm<RemoteJob>("/api/admin/compute/jobs/hechms", form);
      await loadJob(job.id);
    });
  });
  const self = el("button", { disabled: busy }, "Queue a self-test");
  self.title = "The node sleeps a few seconds, logs and returns a small artefact";
  self.addEventListener("click", () =>
    void act(async () => {
      const job = await postJson<RemoteJob>("/api/admin/compute/jobs/selftest", { seconds: 5, lines: 5 });
      await loadJob(job.id);
    }),
  );
  root.append(
    ras,
    hms,
    self,
    el(
      "p",
      { className: "caption" },
      `Zip the whole project folder (inputs up to ${lim ?? "–"} MB). Results come back as a zip ` +
        `(up to ${nodes?.limits.max_result_mb ?? "–"} MB): the plan HDF / DSS output plus summary.json.`,
    ),
  );
}

function drawJobs(root: HTMLElement): void {
  root.append(el("h4", {}, "Remote jobs"));
  if (!jobs.length) {
    root.append(el("p", { className: "caption" }, "No remote jobs yet."));
    return;
  }
  const t = table(["Queued", "Kind", "Status", "Node", "Progress", "Message", ""]);
  for (const j of jobs) {
    const tr = document.createElement("tr");
    const open = el("button", {}, selected?.id === j.id ? "Showing" : "Details");
    open.addEventListener("click", () => void loadJob(j.id));
    tr.append(
      el("td", { className: "caption" }, when(j.created_at)),
      el("td", {}, j.kind),
      el("td", { className: j.status === "error" ? "doc-viewer-error" : "" },
        j.status + (j.results_purged_at ? " · files deleted"
          : j.expiring_soon && (j.has_result || j.has_input) ? ` · files deleted in ${Math.max(0, Math.ceil(j.days_left ?? 0))} d` : "")),
      el("td", { className: "caption" }, j.host ?? "–"),
      el("td", { className: "caption" }, j.progress == null ? "–" : `${Math.round(j.progress * 100)}%`),
      el("td", { className: "caption" }, j.error ?? j.message ?? ""),
      el("td", {}, ""),
    );
    tr.lastElementChild?.append(open);
    t.append(tr);
  }
  root.append(scrollWrap(t));
}

function drawDetail(root: HTMLElement): void {
  const j = selected;
  if (!j) return;
  const box = el("div", { className: "compute-job-detail" });
  box.append(el("h4", {}, `Job ${j.id.slice(0, 8)} — ${j.kind} — ${j.status}`));
  const p = j.params ?? {};
  box.append(
    el(
      "p",
      { className: "caption" },
      `Queued ${when(j.created_at)} by ${j.username ?? "–"}; started ${when(j.started_at)}; finished ${when(j.finished_at)}. ` +
        `Needs: ${j.required_capabilities.join(", ") || "nothing special"}. Attempts: ${j.attempts + (j.started_at ? 1 : 0)}. ` +
        Object.entries(p)
          .filter(([k]) => ["plan_id", "project_file", "run_name", "input_name", "timeout_seconds"].includes(k))
          .map(([k, v]) => `${k}=${String(v)}`)
          .join(" "),
    ),
  );
  if (j.error) box.append(el("p", { className: "doc-viewer-error" }, j.error));
  const buttons = el("div", {});
  if (ACTIVE.has(j.status)) {
    const cancel = el("button", { disabled: busy || !!j.cancel_requested }, j.cancel_requested ? "Cancel requested" : "Cancel");
    cancel.addEventListener("click", () =>
      void act(async () => {
        selected = await postJson<RemoteJob>(`/api/admin/compute/jobs/${j.id}/cancel`, {});
      }),
    );
    buttons.append(cancel);
  }
  // plain same-origin links: the session cookie goes with them
  if (j.has_result) {
    buttons.append(el("a", { href: `/api/admin/compute/jobs/${j.id}/result.zip`, className: "button-link", download: "" }, "Download results (zip)"));
  }
  if (j.has_input) {
    buttons.append(el("a", { href: `/api/admin/compute/jobs/${j.id}/input.zip`, className: "button-link", download: "" }, "Download inputs (zip)"));
  }
  const close = el("button", {}, "Close");
  close.addEventListener("click", () => {
    selected = null;
    redraw();
  });
  buttons.append(close);
  box.append(buttons);
  box.append(retentionNote(j));
  const summary = j.summary ?? j.result?.summary;
  if (summary && Object.keys(summary).length) {
    box.append(el("h5", {}, "Summary"));
    const pre = el("pre", { className: "compute-pre" }, JSON.stringify(summary, null, 1));
    box.append(pre);
  }
  if (j.result_files?.length) {
    box.append(
      el(
        "p",
        { className: "caption" },
        "Result files: " + j.result_files.map((f) => `${f.path} (${(f.size / 1048576).toFixed(1)} MB)`).join(", "),
      ),
    );
  }
  box.append(el("h5", {}, `Log (last ${j.log?.length ?? 0} of ${j.log_total_lines ?? 0} lines)`));
  const pre = el("pre", { className: "compute-pre" }, (j.log ?? []).join("\n"));
  pre.style.maxHeight = "320px";
  pre.style.overflow = "auto";
  box.append(pre);
  root.append(box);
  requestAnimationFrame(() => {
    pre.scrollTop = pre.scrollHeight;
  });
}

/** "Results kept until <date>" (a warning in the last 7 days), or when they were deleted. */
function retentionNote(j: RemoteJob): HTMLElement {
  const days = j.retention_days ?? 30;
  if (j.results_purged_at) {
    return el("p", { className: "caption" },
      `Input and result files were deleted on ${when(j.results_purged_at)} (kept ${days} days after the job finished). The log and summary remain.`);
  }
  if (ACTIVE.has(j.status) || !j.kept_until) {
    return el("p", { className: "caption" }, `Inputs and results are kept for ${days} days after the job finishes, then deleted.`);
  }
  if (!j.has_result && !j.has_input) {
    return el("p", { className: "caption" }, "No input or result files are stored for this job.");
  }
  const until = new Date(j.kept_until).toLocaleDateString(undefined, { dateStyle: "medium" });
  if (j.expiring_soon) {
    const left = Math.max(0, Math.ceil(j.days_left ?? 0));
    return el("p", { className: "doc-viewer-error" },
      `Results kept until ${until}: deleted in ${left} day${left === 1 ? "" : "s"}. Download them now if you need them.`);
  }
  return el("p", { className: "caption" }, `Results kept until ${until} (${days} days after the job finished). Download them before then if you need them.`);
}

function draw(root: HTMLElement): void {
  root.innerHTML = "";
  root.append(el("h3", {}, "Compute nodes"));
  root.append(
    el(
      "p",
      { className: "caption" },
      "Remote machines (e.g. the Windows PC with HEC-RAS / HEC-HMS) that dial in over the tailnet with their own token " +
        `and run jobs the server can't. A node silent for ${nodes?.stale_seconds ?? 180}s has its job requeued once, then failed.`,
    ),
  );
  if (error) root.append(el("p", { className: "doc-viewer-error" }, error));
  drawNodes(root);
  drawEnqueue(root);
  drawJobs(root);
  drawDetail(root);
}

/** Renders the Compute nodes section into `root` (Diagnostics tab). */
export function renderComputeNodesSection(root: HTMLElement): void {
  hostEl = root;
  draw(root);
  void load().then(schedulePoll);
}
